import React, { useState, useEffect } from 'react';
import './Leaderboard.css';

const TIME_RANGES = [
  { id: 'week', label: 'This Week' },
  { id: 'month', label: 'This Month' },
  { id: 'quarter', label: 'This Quarter' }
];

export default function Leaderboard({ token, userRole }) {
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [selectedRange, setSelectedRange] = useState('week');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (token) {
      fetchLeaderboard();
    }
  }, [token, selectedRange]);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`/api/activity/leaderboard?range=${selectedRange}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setLeaderboardData(result.rows || []);
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return '1st';
    if (rank === 2) return '2nd';
    if (rank === 3) return '3rd';
    return `#${rank}`;
  };

  const getScoreColor = (score) => {
    if (score >= 100) return 'score-excellent';
    if (score >= 50) return 'score-good';
    if (score >= 20) return 'score-average';
    return 'score-low';
  };

  if (!token) {
    return (
      <div className="leaderboard-container">
        <div className="access-denied">
          <h3>Access Restricted</h3>
          <p>Please log in to view the leaderboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="leaderboard-container">
      <div className="leaderboard-header">
        <h3>Activity Leaderboard</h3>
        <div className="range-selector">
          {TIME_RANGES.map(range => (
            <button
              key={range.id}
              className={`range-btn ${selectedRange === range.id ? 'active' : ''}`}
              onClick={() => setSelectedRange(range.id)}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="loading-state">
          <div className="loading-spinner">Loading leaderboard...</div>
        </div>
      )}

      {error && (
        <div className="error-state">
          <div className="error-message">Error: {error}</div>
        </div>
      )}

      {!loading && !error && (
        <div className="leaderboard-content">
          {leaderboardData.length === 0 ? (
            <div className="no-data">
              <p>No activity data available for the selected time range.</p>
            </div>
          ) : (
            <div className="leaderboard-table">
              <div className="table-header">
                <div className="header-rank">Rank</div>
                <div className="header-user">User</div>
                <div className="header-score">Score</div>
              </div>
              
              {leaderboardData.map((user, index) => (
                <div key={user._id} className="table-row">
                  <div className="cell-rank">
                    <span className="rank-icon">{getRankIcon(index + 1)}</span>
                  </div>
                  <div className="cell-user">
                    <div className="user-info">
                      <span className="user-name">{user.name || `User ID: ${user._id}`}</span>
                    </div>
                  </div>
                  <div className="cell-score">
                    <span className={`score-value ${getScoreColor(user.total)}`}>
                      {user.total} pts
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
